import React from "react";
import { Radio, ExternalLink } from "lucide-react";

/**
 * LiveBanner — sticky red banner shown on the artist studio dashboard when
 * Paul has an active live session targeted at the user (or global). Renders
 * NOTHING when `session` is falsy, so callers can drop this in unconditionally.
 *
 * Props:
 *   session: { id, title, meetUrl, scope, userId, startedAt } | null
 *   testIdPrefix (optional)
 */
export default function LiveBanner({ session, testIdPrefix = "live-banner" }) {
  if (!session) return null;
  return (
    <div
      data-testid={testIdPrefix}
      className="relative overflow-hidden rounded-2xl border border-red-500/40 bg-gradient-to-r from-red-600/20 via-red-600/10 to-red-500/20 shadow-[0_8px_30px_rgba(220,38,38,0.18)] p-5 md:p-6"
    >
      <div className="absolute inset-0 pointer-events-none opacity-30 mix-blend-screen [background:radial-gradient(800px_120px_at_15%_50%,rgba(220,38,38,0.55),transparent)]" />
      <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="relative flex-shrink-0 mt-0.5">
            <span className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-60" />
            <span className="relative block w-3 h-3 rounded-full bg-red-500" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span
                data-testid={`${testIdPrefix}-tag`}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold bg-red-500 text-white px-2 py-0.5 rounded"
              >
                <Radio className="w-3 h-3" /> Live now
              </span>
              {session.scope === "global" ? (
                <span className="text-[11px] text-red-200/80 uppercase tracking-wider font-semibold">
                  Open session
                </span>
              ) : (
                <span className="text-[11px] text-red-200/80 uppercase tracking-wider font-semibold">
                  1-on-1
                </span>
              )}
            </div>
            <h3
              data-testid={`${testIdPrefix}-title`}
              className="text-lg md:text-xl font-extrabold text-white leading-tight"
            >
              {session.title || "Paul is live"}
            </h3>
            <p className="text-xs md:text-sm text-red-100/70 mt-0.5">
              Tap to join the Google Meet session.
            </p>
          </div>
        </div>
        <a
          data-testid={`${testIdPrefix}-join`}
          href={session.meetUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white font-bold text-sm md:text-base px-5 py-3 rounded-xl transition-colors shadow-md hover:shadow-lg whitespace-nowrap"
        >
          <ExternalLink className="w-4 h-4" />
          Join live session
        </a>
      </div>
    </div>
  );
}
